import { Me, Team } from '@yandex-int/k-common/typings';
import cx from 'classnames';
import get from 'lodash/get';
import { Table, TableHead, TableBody, Text } from 'platform-components';
import { getMe } from 'platform-components/src/components/user/selectors';
import { withRouterPath, InjectedProps } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';

import { deleteClassRequest as deleteClassAction } from 'common.components/user/actions';
import * as userActions from 'common.components/user/actions';
import { getArchivedClasses, getClasses } from 'common.components/user/selectors';

import { ClassRow } from '../__class-row/class__class-row';
import DeleteClassModal from '../__delete-class-modal/class__delete-class-modal';
import { ReduxTeacherStore } from '../../../typings';
import * as classActions from '../actions';
import { getClassIdToDelete } from '../selectors';

import './class__archive-classes.scss';

interface HOCProps extends InjectedProps {}

interface StoreProps {
    archivedClasses: Team[];
    teamId: number;
    classes: Team[];
    classIdToDelete: number | null;
    me: Me | null;
}

interface DispatchProps {
    deleteClassRequest: typeof deleteClassAction;
    getArchivedClassesRequest: typeof userActions.getArchivedClassesRequest;
    recoverClassRequest: typeof userActions.recoverClassRequest;
    setClassIdToDelete: typeof classActions.setClassIdToDelete;
}

interface Props extends HOCProps, StoreProps, DispatchProps {}

export class ArchivedClasses extends React.Component<Props> {
    static contextTypes = {
        staticUrls: PropTypes.object.isRequired,
    };

    state = {
        showDeleteConfirmation: false,
    };

    componentDidMount() {
        const { getArchivedClassesRequest, me } = this.props;

        if (me?.is_teacher) {
            getArchivedClassesRequest();
        }
    }
    context!: React.ContextType<React.Context<{ staticUrls: Record<string, string> }>>;

    handleDeleteClass = ({ id }: Team) => {
        const { setClassIdToDelete } = this.props;

        ym('reachGoal', 'teacher_my_students_class_delete_press');
        setClassIdToDelete(id);
        this.setState({ showDeleteConfirmation: true });
    };

    handleConfirmDeleting = () => {
        ym('reachGoal', 'teacher_my_students_class_delete_success');

        const {
            staticUrls: { labClassStudents, labClasses },
        } = this.context;
        const {
            archivedClasses,
            classes,
            history,
            getRouterPath,
            match: {
                params: { classId },
            },
            classIdToDelete,
            deleteClassRequest,
            setClassIdToDelete,
        } = this.props;
        const isActiveClassExist = classes.length > 0;
        const isDeletingCurrentClass = Number(classId) === Number(classIdToDelete);
        const classItem = archivedClasses.find((item) => item.id === classIdToDelete);

        if (classItem !== undefined) {
            deleteClassRequest(classItem);
        } else {
            console.error('Unexpected undefined delete class request');
        }
        setClassIdToDelete(null);

        if (isDeletingCurrentClass) {
            const url = isActiveClassExist ? getRouterPath(labClassStudents, { classId: classes[0].id }) : labClasses;
            history.push(url);
        }

        this.setState({ showDeleteConfirmation: false });
    };

    handleRejectDeleting = () => {
        const { setClassIdToDelete } = this.props;

        ym('reachGoal', 'teacher_my_students_class_delete_fail');
        setClassIdToDelete(null);
        this.setState({ showDeleteConfirmation: false });
    };

    handleRecoverClass = (classItem: Team) => {
        const { classes, history, getRouterPath, recoverClassRequest } = this.props;
        const {
            staticUrls: { labClassStudents },
        } = this.context;
        const { id } = classItem;

        recoverClassRequest(classItem);
        this.setState({ showRecoverConfirmation: false });

        if (!classes || classes.length === 0) {
            history.push(getRouterPath(labClassStudents, { classId: id }));
        }
    };

    getMenuOptions = () => [
        {
            onClick: this.handleRecoverClass,
            textId: 'class.recover',
        },
        {
            onClick: this.handleDeleteClass,
            textId: 'class.delete',
        },
    ];

    render() {
        const { archivedClasses, teamId, classIdToDelete } = this.props;
        const { showDeleteConfirmation } = this.state;

        if (!archivedClasses || !archivedClasses.length) {
            return null;
        }

        const classToDelete = archivedClasses.find(({ id }) => id === classIdToDelete);

        return (
            <Table mix={cx('class__classes', 'class__archive-classes')}>
                <TableHead mix="class__classes-head">
                    <Text id="class.archiveClass" />
                </TableHead>
                <TableBody mix="class__classes-body">
                    {archivedClasses.map((classItem) => (
                        <ClassRow key={classItem.id} menuOptions={this.getMenuOptions()} value={classItem} />
                    ))}
                </TableBody>
                <DeleteClassModal
                    classId={teamId}
                    level={get(classToDelete, 'level')}
                    name={get(classToDelete, 'name')}
                    onClose={this.handleRejectDeleting}
                    onConfirm={this.handleConfirmDeleting}
                    onReject={this.handleRejectDeleting}
                    opened={showDeleteConfirmation}
                />
            </Table>
        );
    }
}

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: HOCProps): StoreProps => {
    const {
        match: {
            params: { classId },
        },
    } = ownProps;

    return {
        teamId: Number(classId),
        me: getMe(storeState),
        classes: getClasses(storeState),
        archivedClasses: getArchivedClasses(storeState),
        classIdToDelete: getClassIdToDelete(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    const { getArchivedClassesRequest, recoverClassRequest } = userActions;
    const { setClassIdToDelete } = classActions;

    return bindActionCreators(
        {
            deleteClassRequest: deleteClassAction,
            getArchivedClassesRequest,
            recoverClassRequest,
            setClassIdToDelete,
        },
        dispatch
    );
};

export default withRouterPath(connect(mapStateToProps, mapDispatchToProps)(ArchivedClasses));
